import MaterialTable from "@material-table/core";
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Tooltip,
  Typography,
  useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Refresh } from "@material-ui/icons";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import ConfirmDialog from "../components/ConfirmDialog/ConfirmDialog";
import FilterAction from "../components/FilterAction/FilterAction";
import LinkButton from "../components/LinkButton/LinkButton";
import { usePopups } from "../contexts/PopupContext";
import { useGlobalSnackbar } from "../contexts/SnackbarContext";
import { useUser } from "../contexts/UserContextProvider";
import useApi from "../hooks/useApi";
import useLocalStorage from "../hooks/useLocalStorage";
import { useTranslation } from "react-i18next";


const useStyles = makeStyles({
  content: {
    fontSize: "3ex",
  },
  card: {
    padding: "2%",
  },
  title: {
    fontWeight: "lighter",
    paddingBottom: "1%",
  },
  therapName: {
    cursor: "pointer",
    textDecoration: "underline",
  },
});

export default function Therapists() {
  const history = useHistory();
  const classes = useStyles();
  const { t } = useTranslation(["views", "common"]);
  const [items, setItems] = useState([]);
  const [showArchived, setShowArchived] = useLocalStorage(
    "show-archived",
    true,
  );
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const filteredItems = useMemo(
    () => items?.filter((item) => !item.isArchived || showArchived),
    [items, showArchived],
  );

  const [user] = useUser();
  const [therapistToArchive, setTherapistToArchive] = useState(null);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const api = useApi();
  const [, open] = usePopups();

  const setGlobalSnackbar = useGlobalSnackbar();

  const loadTherapists = async () => {
    setLoading(true);
    const response = await api(
      `therapists/clinicalcode/${user?.profile?.clinicCode}`,
      "GET",
      null,
      () => {
        open("sessionExpiredPopup");
      },
    );
    setLoading(false);
    if (response.ok === false) {
      setGlobalSnackbar({
        open: true,
        severity: "error",
        message: `${t("common:errors.therapists")}`,
      });
      return;
    }
    setItems(response);
  };

  useEffect(() => {
    loadTherapists();
  }, []);

  const archiveTherapist = useCallback(async () => {
    if (!therapistToArchive) return;
    const response = await api(
      `therapists/${therapistToArchive?.idTherapist}/archive`,
      "PATCH",
      null,
      () => {
        open("sessionExpiredPopup");
      },
    );
    const snackbar = {
      open: true,
      severity: "error",
      message: `${t("common:errors.archived_user")} ${therapistToArchive?.therapistName} ${therapistToArchive?.therapistLastName}`,
    };
    if (response.ok !== false) {
      loadTherapists();
      snackbar.severity = "success";
      snackbar.message = `${therapistToArchive?.therapistName} ${therapistToArchive?.therapistLastName} ${t("common:success.archived")}`;
      setTherapistToArchive(null);
    }
    setGlobalSnackbar(snackbar);
  }, [therapistToArchive]);

  const closePopup = useCallback(() => setTherapistToArchive(null), []);

  const closeFilter = useCallback(() => {
    setIsFilterPanelOpen((prev) => !prev);
  }, [setIsFilterPanelOpen]);

  return (
    <div className={classes.card}>
      <Typography className={classes.title} variant="h4">
        {t("common:label.therapists")}
      </Typography>
      <div className={classes.content}>
        <MaterialTable
          isLoading={loading}
          theme={theme}
          columns={[
            {
              title: `${t("common:label.therapist")}`,
              field: "therapistFullName",
              customSort: (a, b) => {
                const nameA =
                  `${a.therapistLastName} ${a.therapistName}`.toLowerCase();
                const nameB =
                  `${b.therapistLastName} ${b.therapistName}`.toLowerCase();
                if (nameA < nameB) return -1;
                if (nameA > nameB) return 1;
                return 0;
              },
              customFilterAndSearch: (term, rowData) =>
                `${rowData.therapistLastName} ${rowData.therapistName}`
                  .toLowerCase()
                  .includes(term.toLowerCase()),
              render: (rowData) => (
                <Tooltip title={t("common:tooltip.therapist_profile")}>
                  <span
                    className={classes.therapName}
                    onClick={() =>
                      history.push(`therapists/${rowData.idTherapist}`)
                    }
                  >{`${rowData.therapistLastName} ${rowData.therapistName}`}</span>
                </Tooltip>
              ),
              defaultSort: "asc",
              filterPlaceholder: `${t("views:therapists.filter_placeholder")}`,
            },
            {
              title: `${t("views:therapists.num_patients")}`,
              field: "patientsNumber",
              type: "numeric",
              align: "left",
            },
            {
              title: `${t("views:therapists.archived_patients")}`,
              field: "archivedPatientsNumber",
              type: "numeric",
              align: "left",
            },
            {
              title: `${t("views:therapists.active_patients")}`,
              field: "activePatientsNumber",
              type: "numeric",
              align: "left",
            },
            {
              title: "",
              render: (rowData) => (
                <LinkButton
                  disabled={rowData?.isArchived}
                  onClick={() => setTherapistToArchive(rowData)}
                >
                  {t("common:label.archive")}
                </LinkButton>
              ),
              align: "right",
              width: "10%",
            },
          ]}
          actions={[
            {
              icon: Refresh,
              isFreeAction: true,
              onClick: loadTherapists,
            },
            {
              icon: () => (
                <FilterAction open={isFilterPanelOpen} onClose={closeFilter}>
                  <strong>{t("common:label.filters")}</strong>
                  <Divider />
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={showArchived}
                        onChange={(e) => setShowArchived(e.target.checked)}
                      />
                    }
                    label={t("views:therapists.show_archived_therapist")}
                  />
                </FilterAction>
              ),
              isFreeAction: true,
              onClick: closeFilter,
            },
          ]}
          data={filteredItems}
          options={{
            draggable: false,
            showTitle: false,
            thirdSortClick: false,
            toolbar: true,
            rowStyle: (rowData) => ({
              fontSize: "1rem",
              backgroundColor: rowData?.isArchived ? "#dedede" : null,
              opacity: rowData?.isArchived ? 0.5 : null,
            }),
          }}
          localization={{
            header: {
              actions: "",
            },
            body: {
              emptyDataSourceMessage: (
                <>
                  {t("views:therapists.empty_table_title")} <br />
                  {t("views:therapists.empty_table_subtitle")}
                </>
              ),
            },
            toolbar: {
              searchPlaceholder: `${t("common:table.search_placeholder")}`,
              searchTooltip: `${t("common:table.search_tooltip")}`,
            },
            pagination: {
              labelDisplayedRows: `{from}-{to} ${t("common:table.displayed_rows")} {count}`,
              labelRowsSelect: `${t("common:table.rows")}`,
              labelRowsPerPage: `${t("common:table.rows_page")}`,
              firstTooltip: `${t("common:table.first_tooltip")}`,
              lastTooltip: `${t("common:table.last_tooltip")}`,
              nextTooltip: `${t("common:table.next_tooltip")}`,
              previousTooltip: `${t("common:table.previous_tooltip")}`,
            },
          }}
        />
      </div>
      <ConfirmDialog
        title={`${t("common:info.archived")} ${
          therapistToArchive?.therapistName || ""
        } ${therapistToArchive?.therapistLastName || ""}`}
        open={!!therapistToArchive}
        onCancel={closePopup}
        onClose={closePopup}
        onConfirm={archiveTherapist}
      >
        {t("common:info.archived_message")}
      </ConfirmDialog>
    </div>
  );
}
